import {
  CircularProgress,
  IconButton,
  Link,
  SvgIcon,
  SvgIconProps,
  Tooltip,
} from '@material-ui/core';
import {
  CheckCircle,
  Delete,
  Error,
  Image,
  Refresh,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { mdiTextBox } from '@mdi/js';
import {
  ColorDynamic,
  Column,
  Columns,
  useResponsiveValue,
  useUID,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { forwardRef, memo, ReactNode, useState } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { LocationProtectedIcon } from 'shared/icons/LocationProtectedIcon';
import { PdfIcon } from 'shared/icons/PdfIcon';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';

const FileListItemName = styled.div`
  overflow: hidden;
  line-height: 22px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FileListItemProgress = styled(CircularProgress)`
  font-size: 24px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 14px;
  }
`;

const LocationProtextedText = styled(TextBox)`
  border-bottom: 1px solid ${ColorDynamic.Silver500};
  cursor: pointer;
  display: inline;
  color: ${ColorDynamic.Dark100};
`;

const TextBoxIcon = memo((props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d={mdiTextBox} />
  </SvgIcon>
));

TextBoxIcon.displayName = 'TextBoxIcon';

type FileType = 'pdf' | 'image' | 'unknown';
const PDF_FILE_PATTERN = /\.pdf$/;
const IMAGE_FILE_PATTERN = /\.(gif|png|jpg|jpeg)$/;

function getFileType(name: string): FileType {
  if (PDF_FILE_PATTERN.exec(name)) return 'pdf';
  if (IMAGE_FILE_PATTERN.exec(name)) return 'image';
  return 'unknown';
}

export interface ViewLoadFileListItemProps {
  url?: string;
  name: string;
  guid?: string;
  file?: File;
  gatePassStatus?: 'accessed' | 'not_accessed';
  onRetry?: (file: File) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  helperText?: ReactNode;
  status?: 'idle' | 'loading' | 'error' | 'success';
  onView?: (shouldShow: boolean) => Promise<APIResponse<unknown>>;
  isVisible?: boolean | null;
  createdBy?: 'dispatcher' | 'driver' | 'broker' | '' | null;
  onDownload?: () => void;
  isSubhauled?: boolean;
}

export const ViewLoadFileListItem = forwardRef<
  HTMLDivElement,
  ViewLoadFileListItemProps
>(
  (
    {
      url,
      name,
      guid,
      file,
      status,
      gatePassStatus,
      canDelete = true,
      onRetry,
      onDelete,
      onView,
      isVisible,
      createdBy,
      onDownload,
      isSubhauled,
    },
    ref,
  ) => {
    const uid = useUID();
    const fileType = getFileType(name);
    const [isHoveredState, setIsHovered] = useState(false);
    const isHovered = useResponsiveValue(true, isHoveredState);
    const isGatePassEnabled = useFlag('ctms_gate_pass');
    const [isVisibilityChanging, setIsVisibilityChanging] = useState(false);

    return (
      <div
        ref={ref}
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        aria-label="file-list-item"
        data-testid={`file-list-item-${guid}`}
      >
        <Columns align="center" space="xsmall">
          <Column width="content">
            {gatePassStatus === 'not_accessed' && isGatePassEnabled ? (
              <LocationProtectedIcon fontSize="small" color="primary" />
            ) : status === 'error' ? (
              <Error color="error" fontSize="small" />
            ) : fileType === 'pdf' ? (
              <PdfIcon />
            ) : fileType === 'image' ? (
              <Image htmlColor={ColorDynamic.Dark300} fontSize="small" />
            ) : (
              <TextBoxIcon htmlColor={ColorDynamic.Dark300} fontSize="small" />
            )}
          </Column>

          <Column width="fluid">
            <FileListItemName id={uid}>
              {gatePassStatus === 'not_accessed' && isGatePassEnabled ? (
                <Tooltip
                  interactive={true}
                  title={
                    <>
                      This file is protected by the shipper and can’t be
                      accessed until pickup. It will be available once the
                      driver opens it at the provided pickup location.{' '}
                      <Link
                        href="https://support.superdispatch.com/hc/en-us/articles/47849234437267-How-Gate-Pass-Protection-Works-in-the-Driver-App-and-Carrier-TMS"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                      >
                        Learn more
                      </Link>
                    </>
                  }
                >
                  <LocationProtextedText noWrap={true}>
                    {name}
                  </LocationProtextedText>
                </Tooltip>
              ) : !url ? (
                name
              ) : (
                <Link
                  href={url}
                  noWrap={true}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    onDownload?.();
                  }}
                >
                  {name}
                </Link>
              )}
            </FileListItemName>
          </Column>

          {!isSubhauled && (
            <Column width="content">
              <Tooltip
                arrow={true}
                placement="bottom"
                title={
                  createdBy === 'driver' || createdBy === '' || !!gatePassStatus
                    ? 'Can’t hide files uploaded by driver or location protected by shipper'
                    : ''
                }
              >
                <Box
                  aria-label="view-icon"
                  width={status === 'error' ? '30px' : '60px'}
                >
                  <IconButton
                    size="small"
                    name="icon-button"
                    disabled={
                      createdBy === 'driver' ||
                      createdBy === '' ||
                      !!gatePassStatus
                    }
                    onClick={() => {
                      setIsVisibilityChanging(true);
                      onView?.(!isVisible).finally(() => {
                        setIsVisibilityChanging(false);
                      });
                    }}
                  >
                    {isVisibilityChanging ? (
                      <CircularProgress size="16px" />
                    ) : isVisible ? (
                      <Visibility aria-label="visible-icon" fontSize="small" />
                    ) : (
                      <VisibilityOff
                        aria-label="hidden-icon"
                        fontSize="small"
                      />
                    )}
                  </IconButton>
                </Box>
              </Tooltip>
            </Column>
          )}

          {status === 'error' && (
            <Column width="content">
              <Tooltip title="Retry">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (file) {
                      onRetry?.(file);
                    }
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Column>
          )}

          <Column width="content">
            {status === 'loading' ? (
              <IconButton size="small" disabled={true}>
                <FileListItemProgress size="1em" />
              </IconButton>
            ) : !isHovered && status === 'success' ? (
              <IconButton size="small">
                <CheckCircle
                  fontSize="small"
                  htmlColor={ColorDynamic.Green300}
                />
              </IconButton>
            ) : canDelete ? (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="attachment-delete"
                  size="small"
                  onClick={onDelete}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Box width="22px" />
            )}
          </Column>
        </Columns>
      </div>
    );
  },
);

ViewLoadFileListItem.displayName = 'ViewLoadFileListItem';
